'use client';

import React, { createContext, use, useReducer, useMemo } from 'react';
import type { Startup, DatasetMetrics } from '@/types/data';

type MapState = {
  startups: Startup[];
  metrics: DatasetMetrics | null;
  isLoading: boolean;
  error: string | null;
};

export type MapAction =
  | { type: 'loading' }
  | { type: 'loaded'; startups: Startup[]; metrics: DatasetMetrics | null }
  | { type: 'error'; error: string };

const initialState: MapState = {
  startups: [],
  metrics: null,
  isLoading: true,
  error: null,
};

function mapReducer(state: MapState, action: MapAction): MapState {
  switch (action.type) {
    case 'loading': return { ...state, isLoading: true, error: null };
    case 'loaded': return { ...state, isLoading: false, startups: action.startups, metrics: action.metrics };
    case 'error': return { ...state, isLoading: false, error: action.error };
  }
}

interface MapContextValue extends MapState {
  dispatch: React.Dispatch<MapAction>;
}

const MapContext = createContext<MapContextValue | null>(null);

export const MapProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(mapReducer, initialState);

  const value = useMemo(() => ({ ...state, dispatch }), [state, dispatch]);

  return (
    <MapContext.Provider value={value}>
      {children}
    </MapContext.Provider>
  );
};

export const useMap = (): MapContextValue => {
  const ctx = use(MapContext);
  if (!ctx) throw new Error('useMap must be used inside MapProvider');
  return ctx;
};
